import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Users, LogOut, Plus, Pencil, Trash2, Shield, ArrowLeft, Loader2, X, Key, Tag, Layers, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  finanzas: 'bg-blue-100 text-blue-700',
  contratos: 'bg-purple-100 text-purple-700',
  customer: 'bg-green-100 text-green-700',
  mercado: 'bg-orange-100 text-orange-700',
  admin: 'bg-red-100 text-red-700'
};

const ROLE_LABELS = {
  finanzas: 'Finanzas',
  contratos: 'Contratos',
  customer: 'Cliente',
  mercado: 'Mercado',
  admin: 'Administrador'
};

const PROTECTED_USERS = ['FINANZAS', 'Gerencia'];

const UsuariosPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [builtinRoles, setBuiltinRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [creatingRole, setCreatingRole] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'finanzas',
    extra_modules: [],
    full_name: '',
    email: ''
  });

  const [modulesCatalog, setModulesCatalog] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }
    const parsed = JSON.parse(userData);
    // Only Gerencia (contratos role) can access this page
    if (parsed.role !== 'contratos' && parsed.username !== 'Gerencia') {
      toast.error('Acceso restringido a Gerencia');
      navigate('/login');
      return;
    }
    setUser(parsed);
    loadUsers();
    loadRoles();
    loadModulesCatalog();
  }, [navigate]);

  const loadModulesCatalog = async () => {
    try {
      const r = await axios.get(`${API}/users/modules-catalog`);
      setModulesCatalog(r.data.modules || []);
    } catch {
      setModulesCatalog([
        { key: 'finanzas', label: 'Portal Finanzas' },
        { key: 'contratos', label: 'Portal Contratos' },
        { key: 'qa', label: 'Portal QA' },
        { key: 'mercado', label: 'Portal Mercado' },
        { key: 'customer', label: 'Portal Customer' },
        { key: 'usuarios', label: 'Gestion de Usuarios' },
        { key: 'email_config', label: 'Configuracion de Emails' },
      ]);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/`);
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const res = await axios.get(`${API}/users/roles`);
      setRoles(res.data.roles || []);
      setBuiltinRoles(res.data.builtin || []);
      setCustomRoles(res.data.custom || []);
    } catch {
      setRoles(['finanzas', 'contratos', 'customer', 'mercado', 'admin']);
    }
  };

  const handleCreateRole = async () => {
    const name = newRoleName.trim();
    if (!name || name.length < 2) {
      toast.error('Nombre del rol invalido');
      return;
    }
    setCreatingRole(true);
    try {
      await axios.post(`${API}/users/roles`, { name, description: newRoleDesc });
      toast.success(`Rol "${name}" creado`);
      setNewRoleName('');
      setNewRoleDesc('');
      loadRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando rol');
    }
    setCreatingRole(false);
  };

  const handleDeleteRole = async (name) => {
    if (!window.confirm(`Eliminar el rol "${name}"?`)) return;
    try {
      await axios.delete(`${API}/users/roles/${name}`);
      toast.success(`Rol "${name}" eliminado`);
      loadRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error eliminando');
    }
  };

  const _defaultRole = () => {
    // Use first builtin, then first custom, then any role; never hardcode to finanzas.
    if (builtinRoles.length) return builtinRoles[0];
    if (customRoles.length) return customRoles[0].name || customRoles[0];
    if (roles.length) return roles[0];
    return '';
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', role: _defaultRole(), extra_modules: [], full_name: '', email: '' });
    setShowModal(true);
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setFormData({
      username: u.username,
      password: '',
      role: u.role,
      extra_modules: u.extra_modules || [],
      full_name: u.full_name || '',
      email: u.email || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'finanzas', extra_modules: [], full_name: '', email: '' });
  };

  const toggleModule = (k) => {
    setFormData(f => ({
      ...f,
      extra_modules: f.extra_modules.includes(k)
        ? f.extra_modules.filter(x => x !== k)
        : [...f.extra_modules, k],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username.trim()) {
      toast.error('El nombre de usuario es obligatorio');
      return;
    }
    if (!editingUser && !formData.password.trim()) {
      toast.error('La contraseña es obligatoria al crear un usuario');
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { role: formData.role, extra_modules: formData.extra_modules };
        if (formData.username !== editingUser.username) payload.username = formData.username;
        if (formData.password) payload.password = formData.password;
        if (formData.full_name !== (editingUser.full_name || '')) payload.full_name = formData.full_name;
        if (formData.email !== (editingUser.email || '')) payload.email = formData.email;
        await axios.put(`${API}/users/${editingUser.username}`, payload);
        toast.success('Usuario actualizado correctamente');
      } else {
        await axios.post(`${API}/users/`, formData);
        toast.success('Usuario creado correctamente');
      }
      closeModal();
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (username) => {
    if (PROTECTED_USERS.includes(username)) {
      toast.error('Este usuario del sistema no puede eliminarse');
      return;
    }
    if (!window.confirm(`¿Eliminar al usuario "${username}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/users/${username}`);
      toast.success('Usuario eliminado');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#2F3D8E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="usuarios-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/contratos')} data-testid="back-to-contratos">
                <ArrowLeft className="w-4 h-4 mr-1" /> Contratos
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2F3D8E] flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#2F3D8E]">Gestión de Usuarios</h1>
                  <p className="text-sm text-gray-500">Administra los accesos al sistema</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Bienvenido, <strong>{user?.username}</strong>
              </span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" /> Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              {users.length} {users.length === 1 ? 'usuario registrado' : 'usuarios registrados'}
            </h2>
            <p className="text-sm text-gray-500">Crea, edita o elimina accesos a los portales</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/configuracion/emails')}
              variant="outline"
              className="border-[#4C8C3B] text-[#4C8C3B] hover:bg-[#4C8C3B] hover:text-white"
              data-testid="email-config-btn"
            >
              <Mail className="w-4 h-4 mr-2" /> Configurar Emails
            </Button>
            <Button
              onClick={() => setShowRolesModal(true)}
              variant="outline"
              className="border-[#2F3D8E] text-[#2F3D8E] hover:bg-[#2F3D8E] hover:text-white"
              data-testid="manage-roles-btn"
            >
              <Tag className="w-4 h-4 mr-2" /> Gestionar Roles
            </Button>
            <Button
              onClick={openCreateModal}
              className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white"
              data-testid="create-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Usuario</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Rol</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Estado</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center py-12 text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    No hay usuarios registrados
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.username} className="border-b hover:bg-gray-50 transition-colors" data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2F3D8E] to-[#5DA5D8] flex items-center justify-center text-white text-sm font-bold">
                        {u.username.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-800">{u.username}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-700'}`}>
                      <Shield className="w-3 h-3" />
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                    {(u.extra_modules || []).length > 0 && (
                      <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                        <Layers className="w-2.5 h-2.5" /> +{u.extra_modules.length} modulo(s):{' '}
                        <span className="font-mono text-[10px] text-[#2F3D8E]">{u.extra_modules.join(', ')}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {PROTECTED_USERS.includes(u.username) ? (
                      <span className="text-xs text-gray-500 italic">Sistema</span>
                    ) : (
                      <span className="text-xs text-green-600 font-medium">Activo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(u)}
                        data-testid={`edit-user-${u.username}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(u.username)}
                        disabled={PROTECTED_USERS.includes(u.username)}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                        data-testid={`delete-user-${u.username}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* Modal Create / Edit */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-bold text-[#2F3D8E]">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de usuario
                </label>
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="ej: Juan.Perez"
                  required
                  data-testid="username-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Key className="w-3 h-3" />
                  Contraseña {editingUser && <span className="text-xs text-gray-400 ml-1">(dejar vacío para mantener)</span>}
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? '••••••••' : 'Mínimo 6 caracteres'}
                  minLength={editingUser ? 0 : 6}
                  data-testid="password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rol / Portal
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F3D8E]"
                  data-testid="role-select"
                >
                  {builtinRoles.length > 0 && (
                    <optgroup label="Roles del sistema">
                      {builtinRoles.map(r => (
                        <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                      ))}
                    </optgroup>
                  )}
                  {customRoles.length > 0 && (
                    <optgroup label="Roles personalizados">
                      {customRoles.map(r => (
                        <option key={r.name || r} value={r.name || r}>
                          {(r.name || r)}{r.description ? ` · ${r.description}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {/* Fallback when nothing is loaded */}
                  {builtinRoles.length === 0 && customRoles.length === 0 && roles.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  <strong>Seleccionado:</strong> <span className="font-mono">{formData.role}</span> · Los modulos del usuario seran los marcados abajo, sin importar el rol "portal" elegido aqui.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Layers className="w-3 h-3" /> Modulos adicionales (acceso secundario)
                </label>
                <div className="border rounded-md p-2 bg-gray-50 grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto" data-testid="modules-multiselect">
                  {modulesCatalog.map(m => {
                    const checked = formData.extra_modules.includes(m.key);
                    return (
                      <label key={m.key}
                             className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer hover:bg-white border ${checked ? 'border-[#2F3D8E] bg-white' : 'border-transparent'}`}
                             data-testid={`module-checkbox-${m.key}`}>
                        <input type="checkbox" checked={checked}
                               onChange={() => toggleModule(m.key)}
                               className="w-3.5 h-3.5 accent-[#2F3D8E]" />
                        <span className={checked ? 'font-semibold text-[#2F3D8E]' : 'text-gray-700'}>{m.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Marca uno o varios modulos a los que tendra acceso adicional, ademas de su portal principal.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre completo</label>
                  <Input value={formData.full_name}
                         onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                         placeholder="Opcional" data-testid="full-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <Input type="email" value={formData.email}
                         onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                         placeholder="Opcional" data-testid="email-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeModal} disabled={saving}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white"
                  data-testid="save-user-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gestionar Roles */}
      {showRolesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="roles-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="bg-[#2F3D8E] text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Tag className="w-5 h-5" /> Gestionar Roles
              </h3>
              <button onClick={() => setShowRolesModal(false)} className="hover:bg-white/10 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Crear rol personalizado</h4>
                <div className="grid grid-cols-1 gap-2">
                  <Input
                    placeholder="nombre_del_rol (sin espacios)"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    data-testid="role-name-input"
                  />
                  <Input
                    placeholder="Descripcion (opcional)"
                    value={newRoleDesc}
                    onChange={(e) => setNewRoleDesc(e.target.value)}
                    data-testid="role-desc-input"
                  />
                  <Button
                    onClick={handleCreateRole}
                    disabled={creatingRole}
                    className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white"
                    data-testid="save-role-btn"
                  >
                    {creatingRole ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Crear rol
                  </Button>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Roles personalizados existentes</h4>
                {customRoles.length === 0 ? (
                  <p className="text-sm text-gray-400">Aun no hay roles personalizados.</p>
                ) : (
                  <ul className="space-y-2">
                    {customRoles.map(r => (
                      <li key={r.name} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`custom-role-${r.name}`}>
                        <div>
                          <div className="font-semibold text-sm">{r.name}</div>
                          {r.description && <div className="text-xs text-gray-500">{r.description}</div>}
                        </div>
                        <Button size="sm" variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleDeleteRole(r.name)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Roles del sistema</h4>
                <div className="flex flex-wrap gap-1.5">
                  {builtinRoles.map(r => (
                    <span key={r} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full border">{r}</span>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2 italic">Los roles del sistema no se pueden eliminar.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosPage;
